/**
 * @rogueworlds/voxel-bridge — chunk storage, chunk address math and the memory-cached world view
 * (generated terrain + a sparse authoritative edit overlay).
 *
 * Two storage representations matter here and they are NOT the same thing:
 * - in memory a chunk is a `Uint8Array` of PALETTE INDICES (`Chunk.blocks`) plus `Chunk.palette`
 *   (index -> `BlockId`, `palette[0]` always AIR), which keeps the per-chunk footprint at 32 KiB;
 * - on the wire a snapshot carries the literal BLOCK ID stream (`ChunkSnapshot.blocks`,
 *   `base64(rle(Uint8Array(CHUNK_VOXELS)))` per CONTRACTS.md) plus the palette it was built from.
 *   `serialize()` therefore expands indices to ids; `deserialize()` re-derives the canonical
 *   palette (unique ids present, ascending, AIR forced to index 0).
 *
 * ISOMORPHIC: no DOM, no `three`, no Node built-ins.
 */

import {
  CHUNK_SIZE,
  CHUNK_VOXELS,
  encodeChunkKey,
  fromBase64,
  rleDecode,
  rleEncode,
  toBase64,
} from "@rogueworlds/shared";
import type { BlockId, ChunkKey, ChunkSnapshot, WorldConfig } from "@rogueworlds/shared";

import { AIR } from "./blocks.js";
import { buildHeightMap, sampleBlock, sampleBlockWithHeightMap } from "./worldgen.js";

/** Highest palette size a chunk can address (`Chunk.blocks` is one byte per voxel). */
const MAX_PALETTE = 256;

/**
 * Index of a local voxel inside a chunk, x fastest then z then y:
 * `((ly * 32) + lz) * 32 + lx`, always in `0..32767` for in-range input.
 */
export function chunkIndex(lx: number, ly: number, lz: number): number {
  return (ly * CHUNK_SIZE + lz) * CHUNK_SIZE + lx;
}

/** Chunk origin in absolute voxel coordinates. */
export function voxelOfChunk(
  cx: number,
  cy: number,
  cz: number,
): { bx: number; by: number; bz: number } {
  return { bx: cx * CHUNK_SIZE, by: cy * CHUNK_SIZE, bz: cz * CHUNK_SIZE };
}

/** Chunk coordinate that contains a voxel coordinate. Negative coordinates floor correctly. */
export function chunkCoordOfVoxel(v: number): number {
  return Math.floor(v / CHUNK_SIZE);
}

/** Canonical `ChunkKey` of the chunk containing an absolute voxel coordinate. */
export function chunkKeyOfVoxel(bx: number, by: number, bz: number): ChunkKey {
  return encodeChunkKey(chunkCoordOfVoxel(bx), chunkCoordOfVoxel(by), chunkCoordOfVoxel(bz));
}

/** Local voxel coordinates of a chunk-local linear index. Inverse of `chunkIndex` for one size. */
function localOfIndex(index: number, size: number): { lx: number; ly: number; lz: number } {
  const area = size * size;
  return {
    lx: index % size,
    lz: Math.floor(index / size) % size,
    ly: Math.floor(index / area),
  };
}

/**
 * Canonical palette build: unique block ids actually present, ascending, with AIR forced to
 * index 0. Indexed `Uint8Array` presence flags are used deliberately — never a `Set`/`Map`
 * iteration — so the result cannot depend on insertion order (DET-1).
 *
 * @internal exported for `worldgen.generateChunk`; not part of the frozen public surface.
 */
export function buildPalette(ids: Uint8Array): { palette: number[]; blocks: Uint8Array } {
  const present = new Uint8Array(MAX_PALETTE);
  for (let i = 0; i < ids.length; i++) {
    present[ids[i] ?? AIR] = 1;
  }
  const palette: number[] = [AIR];
  const indexOfId = new Uint8Array(MAX_PALETTE);
  for (let id = 1; id < MAX_PALETTE; id++) {
    if ((present[id] ?? 0) !== 0) {
      indexOfId[id] = palette.length;
      palette.push(id);
    }
  }
  const blocks = new Uint8Array(ids.length);
  for (let i = 0; i < ids.length; i++) {
    blocks[i] = indexOfId[ids[i] ?? AIR] ?? 0;
  }
  return { palette, blocks };
}

/** Storage handed to the constructor by `generateChunk` / `Chunk.deserialize`. @internal */
export interface ChunkInit {
  readonly palette: number[];
  readonly blocks: Uint8Array;
  readonly rev: number;
}

/**
 * A cubic voxel chunk. `blocks` holds palette indices; `palette[0]` is always AIR, so a freshly
 * allocated (zero-filled) chunk is an air chunk.
 */
export class Chunk {
  readonly cx: number;
  readonly cy: number;
  readonly cz: number;
  readonly size: number;
  /** Palette indices, `size ** 3` bytes, x fastest then z then y. */
  readonly blocks: Uint8Array;
  /** Index -> `BlockId`. `palette[0]` is ALWAYS AIR and is never reordered. */
  readonly palette: number[];
  /**
   * Revision counter carried through `serialize`/`deserialize`. The host's edit log bumps it per
   * persisted change; the voxel bridge itself never does. Additive to the frozen surface (a
   * `ChunkSnapshot.rev` has to come from somewhere) — every other member matches CONTRACTS.md.
   */
  rev: number;

  constructor(cx: number, cy: number, cz: number, size: number = CHUNK_SIZE, init?: ChunkInit) {
    if (!Number.isInteger(cx) || !Number.isInteger(cy) || !Number.isInteger(cz)) {
      throw new TypeError(`Chunk: chunk coordinates must be integers, got (${cx}, ${cy}, ${cz})`);
    }
    if (!Number.isInteger(size) || size < 1) {
      throw new RangeError(`Chunk: size must be a positive integer, got ${size}`);
    }
    const volume = size * size * size;
    if (init !== undefined && init.blocks.length !== volume) {
      throw new RangeError(
        `Chunk: init blocks length ${init.blocks.length} does not match size ${size} (${volume} voxels)`,
      );
    }
    this.cx = cx;
    this.cy = cy;
    this.cz = cz;
    this.size = size;
    this.blocks = init === undefined ? new Uint8Array(volume) : init.blocks;
    this.palette = init === undefined ? [AIR] : init.palette;
    this.rev = init === undefined ? 0 : init.rev;
  }

  /** Chunk-local voxel -> linear index, or -1 when out of range (no clamping, no throwing). */
  private index(lx: number, ly: number, lz: number): number {
    const size = this.size;
    if (!Number.isInteger(lx) || !Number.isInteger(ly) || !Number.isInteger(lz)) {
      return -1;
    }
    if (lx < 0 || ly < 0 || lz < 0 || lx >= size || ly >= size || lz >= size) {
      return -1;
    }
    return (ly * size + lz) * size + lx;
  }

  /** Block at a local coordinate. Out-of-range reads are AIR (chunk-local reads never wrap). */
  get(lx: number, ly: number, lz: number): BlockId {
    const index = this.index(lx, ly, lz);
    if (index < 0) {
      return AIR;
    }
    return this.palette[this.blocks[index] ?? 0] ?? AIR;
  }

  /**
   * Sets a local voxel and returns true only when the stored block actually changed. Out-of-range
   * calls are ignored (false). The palette grows on demand; a chunk built by `generateChunk` or
   * `deserialize` is ascending, an edited one keeps the palette order it was created with.
   */
  set(lx: number, ly: number, lz: number, block: BlockId): boolean {
    const index = this.index(lx, ly, lz);
    if (index < 0) {
      return false;
    }
    if (!Number.isInteger(block) || block < 0 || block >= MAX_PALETTE) {
      throw new RangeError(`Chunk.set: block must be an integer in 0..255, got ${block}`);
    }
    const current = this.palette[this.blocks[index] ?? 0] ?? AIR;
    if (current === block) {
      return false;
    }
    let paletteIndex = this.palette.indexOf(block);
    if (paletteIndex < 0) {
      if (this.palette.length >= MAX_PALETTE) {
        throw new RangeError(`Chunk.set: palette of ${this.palette.length} entries is full`);
      }
      paletteIndex = this.palette.length;
      this.palette.push(block);
    }
    this.blocks[index] = paletteIndex;
    return true;
  }

  /**
   * Serializes to the wire snapshot: `blocks` is the expanded BLOCK ID stream (RLE + base64), plus
   * the palette and the revision. See the file header for why ids and not indices.
   */
  serialize(): ChunkSnapshot {
    const ids = new Uint8Array(this.blocks.length);
    for (let i = 0; i < ids.length; i++) {
      ids[i] = this.palette[this.blocks[i] ?? 0] ?? AIR;
    }
    return {
      key: encodeChunkKey(this.cx, this.cy, this.cz),
      cx: this.cx,
      cy: this.cy,
      cz: this.cz,
      rev: this.rev,
      palette: this.palette.slice(),
      blocks: toBase64(rleEncode(ids)),
    };
  }

  /** Alias of `serialize()` — both names are in the frozen surface. */
  toSnapshot(): ChunkSnapshot {
    return this.serialize();
  }

  /** Rebuilds a chunk from a snapshot. Every voxel and the revision survive the round trip. */
  static deserialize(snapshot: ChunkSnapshot): Chunk {
    if (typeof snapshot !== "object" || snapshot === null) {
      throw new TypeError("Chunk.deserialize expects a ChunkSnapshot");
    }
    if (!Number.isInteger(snapshot.rev)) {
      throw new TypeError(`Chunk.deserialize: rev must be an integer, got ${String(snapshot.rev)}`);
    }
    if (!Array.isArray(snapshot.palette)) {
      throw new TypeError("Chunk.deserialize: palette must be an array of block ids");
    }
    if (!Number.isInteger(snapshot.cx) || !Number.isInteger(snapshot.cy) || !Number.isInteger(snapshot.cz)) {
      throw new TypeError("Chunk.deserialize: chunk coordinates must be integers");
    }
    const ids = rleDecode(fromBase64(snapshot.blocks), CHUNK_VOXELS);
    const built = buildPalette(ids);
    return new Chunk(snapshot.cx, snapshot.cy, snapshot.cz, CHUNK_SIZE, {
      palette: built.palette,
      blocks: built.blocks,
      rev: snapshot.rev,
    });
  }
}

/**
 * Generates one chunk from `(seed, cx, cy, cz)` — pure and byte-identical across runtimes.
 *
 * Every one of the 32768 voxels is sampled through the same code path as `sampleBlock` (only the
 * column heights are precomputed for the footprint plus a 1-voxel border, because the tree rule
 * needs the 9 neighbouring columns). `blocks` holds indices into `palette`, which is the unique
 * set of ids present in ascending order with AIR forced to index 0.
 *
 * Absolute coordinates mean a chunk at a negative `cx`/`cz` generates on the same lattice grid as
 * its positive neighbours, so the world is seamless.
 */
export function generateChunk(cx: number, cy: number, cz: number, seed: number): Chunk {
  const size = CHUNK_SIZE;
  const map = buildHeightMap(cx, cz, size, seed);
  const originX = cx * size;
  const originY = cy * size;
  const originZ = cz * size;
  const ids = new Uint8Array(size * size * size);
  let cursor = 0;
  // x fastest, then z, then y — the chunk index order.
  for (let ly = 0; ly < size; ly++) {
    for (let lz = 0; lz < size; lz++) {
      for (let lx = 0; lx < size; lx++) {
        ids[cursor] = sampleBlockWithHeightMap(originX + lx, originY + ly, originZ + lz, seed, map);
        cursor++;
      }
    }
  }
  const built = buildPalette(ids);
  return new Chunk(cx, cy, cz, size, { palette: built.palette, blocks: built.blocks, rev: 0 });
}

/** Applies chunk-local edits to a chunk in order (later entries supersede earlier ones). */
export function applyEditsToChunk(chunk: Chunk, edits: readonly VoxelEdit[]): void {
  const size = chunk.size;
  const limit = size * size * size;
  for (let i = 0; i < edits.length; i++) {
    const edit = edits[i];
    if (edit === undefined) {
      continue;
    }
    if (!Number.isInteger(edit.idx) || edit.idx < 0 || edit.idx >= limit) {
      throw new RangeError(`applyEditsToChunk: idx ${edit.idx} is outside a ${size}^3 chunk`);
    }
    const local = localOfIndex(edit.idx, size);
    chunk.set(local.lx, local.ly, local.lz, edit.block);
  }
}

/** A single authoritative edit, addressed by chunk-local linear index. */
export interface VoxelEdit {
  readonly idx: number;
  readonly block: BlockId;
}

/** The same edit addressed in absolute voxel coordinates (what a mesher job carries). */
export interface VoxelEditAbs {
  readonly bx: number;
  readonly by: number;
  readonly bz: number;
  readonly block: BlockId;
}

const NO_EDITS: readonly VoxelEdit[] = Object.freeze([]);

/**
 * The world as the client and the authority both see it: deterministic generated terrain with a
 * sparse edit overlay layered on top, plus an optional memory cache of generated chunks.
 *
 * Read order for `getBlock` is overlay -> cached chunk -> generated terrain, so an edit always
 * wins; the same edit is also applied to a cached chunk, which is what makes a mesh built from
 * `ensureChunk` agree with `getBlock` voxel for voxel.
 */
export class VoxelWorld {
  readonly seed: number;

  private readonly chunkSize: number;
  /** Cached chunks, keyed canonically. Iteration order is insertion order (deterministic). */
  private readonly chunks: Map<ChunkKey, Chunk> = new Map<ChunkKey, Chunk>();
  /** Edit overlay: chunk key -> edits in application order (later entries supersede earlier). */
  private readonly overlay: Map<ChunkKey, VoxelEdit[]> = new Map<ChunkKey, VoxelEdit[]>();

  constructor(cfg: WorldConfig, seed: number) {
    if (!Number.isInteger(seed)) {
      throw new TypeError(`VoxelWorld: seed must be an integer, got ${seed}`);
    }
    if (!Number.isInteger(cfg.chunkSize) || cfg.chunkSize < 1) {
      throw new RangeError(`VoxelWorld: cfg.chunkSize must be a positive integer, got ${cfg.chunkSize}`);
    }
    if (cfg.chunkSize !== CHUNK_SIZE) {
      // `generateChunk`/`chunkIndex`/`Chunk.deserialize` are frozen at CHUNK_SIZE; any other size
      // here would silently mis-address voxels, so fail loud instead.
      throw new RangeError(
        `VoxelWorld: the voxel bridge is frozen at CHUNK_SIZE=${CHUNK_SIZE}, got cfg.chunkSize=${cfg.chunkSize}`,
      );
    }
    this.seed = seed;
    this.chunkSize = cfg.chunkSize;
  }

  /** Chunk key + local coordinates of an absolute voxel coordinate. */
  private locate(bx: number, by: number, bz: number): { key: ChunkKey; lx: number; ly: number; lz: number } {
    const size = this.chunkSize;
    const cx = Math.floor(bx / size);
    const cy = Math.floor(by / size);
    const cz = Math.floor(bz / size);
    return { key: encodeChunkKey(cx, cy, cz), lx: bx - cx * size, ly: by - cy * size, lz: bz - cz * size };
  }

  private overlayGet(key: ChunkKey, idx: number): BlockId | undefined {
    const list = this.overlay.get(key);
    if (list === undefined) {
      return undefined;
    }
    // Later edits supersede earlier ones, so scan backwards and stop at the first match.
    for (let i = list.length - 1; i >= 0; i--) {
      const edit = list[i];
      if (edit !== undefined && edit.idx === idx) {
        return edit.block;
      }
    }
    return undefined;
  }

  private overlaySet(key: ChunkKey, idx: number, block: BlockId): void {
    const existing = this.overlay.get(key);
    if (existing === undefined) {
      this.overlay.set(key, [{ idx, block }]);
      return;
    }
    for (let i = 0; i < existing.length; i++) {
      const edit = existing[i];
      if (edit !== undefined && edit.idx === idx) {
        existing[i] = { idx, block };
        return;
      }
    }
    existing.push({ idx, block });
  }

  /** Block at an absolute voxel coordinate: edit overlay, else cached chunk, else terrain. */
  getBlock(bx: number, by: number, bz: number): BlockId {
    const { key, lx, ly, lz } = this.locate(bx, by, bz);
    const edit = this.overlayGet(key, (ly * this.chunkSize + lz) * this.chunkSize + lx);
    if (edit !== undefined) {
      return edit;
    }
    const chunk = this.chunks.get(key);
    if (chunk !== undefined) {
      return chunk.get(lx, ly, lz);
    }
    return sampleBlock(bx, by, bz, this.seed);
  }

  /** Records an authoritative edit. Returns true only when the visible block actually changed. */
  setBlock(bx: number, by: number, bz: number, block: BlockId): boolean {
    if (!Number.isInteger(block) || block < 0 || block >= MAX_PALETTE) {
      throw new RangeError(`VoxelWorld.setBlock: block must be an integer in 0..255, got ${block}`);
    }
    if (this.getBlock(bx, by, bz) === block) {
      return false;
    }
    const { key, lx, ly, lz } = this.locate(bx, by, bz);
    this.overlaySet(key, (ly * this.chunkSize + lz) * this.chunkSize + lx, block);
    const chunk = this.chunks.get(key);
    if (chunk !== undefined) {
      chunk.set(lx, ly, lz, block);
    }
    return true;
  }

  /** Cached chunk for a key, or undefined when it has not been generated yet. */
  getChunk(key: ChunkKey): Chunk | undefined {
    return this.chunks.get(key);
  }

  /** Generates (once) and caches the chunk, with the edit overlay already applied to it. */
  ensureChunk(cx: number, cy: number, cz: number): Chunk {
    const key = encodeChunkKey(cx, cy, cz);
    const cached = this.chunks.get(key);
    if (cached !== undefined) {
      return cached;
    }
    const chunk = generateChunk(cx, cy, cz, this.seed);
    const edits = this.overlay.get(key);
    if (edits !== undefined) {
      applyEditsToChunk(chunk, edits);
    }
    this.chunks.set(key, chunk);
    return chunk;
  }

  /** Applies edits to the overlay and to the cached chunk (if any) for `key`. */
  applyEdits(key: ChunkKey, edits: readonly VoxelEdit[]): void {
    const size = this.chunkSize;
    const limit = size * size * size;
    for (let i = 0; i < edits.length; i++) {
      const edit = edits[i];
      if (edit === undefined) {
        continue;
      }
      if (!Number.isInteger(edit.idx) || edit.idx < 0 || edit.idx >= limit) {
        throw new RangeError(`VoxelWorld.applyEdits: idx ${edit.idx} is outside a ${size}^3 chunk`);
      }
      this.overlaySet(key, edit.idx, edit.block);
    }
    const chunk = this.chunks.get(key);
    if (chunk !== undefined) {
      applyEditsToChunk(chunk, edits);
    }
  }

  /** Overlay edits for one chunk, in application order. Treat the result as read-only. */
  editsFor(key: ChunkKey): readonly VoxelEdit[] {
    return this.overlay.get(key) ?? NO_EDITS;
  }

  /**
   * Overlay edits for this chunk and its 6 face neighbours, in absolute voxel coordinates — the
   * slice a mesher job needs so boundary faces cull against edited neighbours.
   */
  editsNear(cx: number, cy: number, cz: number): readonly VoxelEditAbs[] {
    const out: VoxelEditAbs[] = [];
    this.collectEdits(out, cx, cy, cz);
    this.collectEdits(out, cx + 1, cy, cz);
    this.collectEdits(out, cx - 1, cy, cz);
    this.collectEdits(out, cx, cy + 1, cz);
    this.collectEdits(out, cx, cy - 1, cz);
    this.collectEdits(out, cx, cy, cz + 1);
    this.collectEdits(out, cx, cy, cz - 1);
    return out;
  }

  private collectEdits(out: VoxelEditAbs[], cx: number, cy: number, cz: number): void {
    const list = this.overlay.get(encodeChunkKey(cx, cy, cz));
    if (list === undefined) {
      return;
    }
    const size = this.chunkSize;
    const originX = cx * size;
    const originY = cy * size;
    const originZ = cz * size;
    for (let i = 0; i < list.length; i++) {
      const edit = list[i];
      if (edit === undefined) {
        continue;
      }
      const local = localOfIndex(edit.idx, size);
      out.push({
        bx: originX + local.lx,
        by: originY + local.ly,
        bz: originZ + local.lz,
        block: edit.block,
      });
    }
  }

  /** Visits every cached chunk in insertion order. */
  forEachLoadedChunk(cb: (chunk: Chunk, key: ChunkKey) => void): void {
    this.chunks.forEach((chunk, key) => {
      cb(chunk, key);
    });
  }
}
